import React from 'react';
import propTypes from 'prop-types';
import { Form } from 'shared-ui';
import { isDefined, isValidDate, ObjectAccessor, formatDate } from '@dateam/ark';
import { concatClassName } from '@dateam/ark-react';
import { plotDetailsPropTypes } from 'data/plot';
import { validate as validator, ValidationError } from 'data/observation';
import { ObservationProps } from './types';
import { ObservationErrors } from './components';
import ObservationField from './ObservationField';
import ObservationItem from './ObservationItem';
import styles from './Observation.module.scss';

const observationKey = 'observation24';

type Props = ObservationProps<App.Observation24>;

const Observation24: React.FC<Props> = ({
    data,
    plot,
    ignorable,
    className,
    onChange
}: Props) => {
    const [validationErrors, setValidationErrors] = React.useState<Record<string, ValidationError>>({});
    const internalData = React.useRef(data);

    React.useEffect(() => {
        internalData.current = data;
    }, [data]);

    React.useEffect(() => {
        if (data.count === undefined &&
            data.total === undefined) return;

        setValidationErrors(validator(data, plot));
    }, [plot, data]);

    const count = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.count ?? undefined;
    }, [data]);

    const total = React.useMemo(() => {
        if (data.ignored) return undefined;

        return data.total ?? undefined;
    }, [data]);

    const previousValue = React.useMemo(() => {
        if (isDefined(internalData.current.previous)) {
            return (
                <>
                    {isValidDate(internalData.current.previous.updatedOn) && (
                        <div className={styles['previousDate']}>{formatDate(internalData.current.previous.updatedOn)}</div>
                    )}
                    <div className={styles['previous']}>Comptage: {internalData.current.previous.count?.value ?? '-'}</div>
                    <div className={styles['previous']}>Total /10 feuilles: {internalData.current.previous.total?.value ?? '-'}</div>
                </>
            );
        }

        return (<div>Aucune valeur disponible</div>);
    }, [internalData.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleIgnoreObservation = React.useCallback(() => {
        const newData: App.Observation24 = {
            ...internalData.current,
            count: null,
            total: null,
            ignored: !internalData.current.ignored
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleCountChange = React.useCallback((newValue: number | undefined) => {
        const newData: App.Observation24 = {
            ...internalData.current,
            count: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleTotalChange = React.useCallback((newValue: number | undefined) => {
        const newData: App.Observation24 = {
            ...internalData.current,
            total: newValue ?? null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    const handleFocus = React.useCallback((field: ObjectAccessor<Omit<App.Observation24, 'previous'>>) => {
        if (internalData.current[field] !== undefined) return;

        const newData: App.Observation24 = {
            ...internalData.current,
            [field]: null
        };

        onChange?.(newData);
    }, [onChange, internalData]);

    return (
        <ObservationItem
            data={data}
            ignorable={ignorable}
            onIgnore={handleIgnoreObservation}
            previous={previousValue}
            label="Typhlodromes"
        >
            <div className="row">
                <ObservationField
                    id={`${observationKey}-count`}
                    label="Comptage"
                    offLabel="/10 feuilles"
                    className={concatClassName(styles[`${observationKey}-count`], 'col-4')}
                >
                    <Form.Number
                        min={0}
                        max={10}
                        value={count}
                        onChange={handleCountChange}
                        state={validationErrors['count']?.type}
                        onFocus={() => handleFocus('count')}
                        required={!data.ignored}
                        readOnly={data.ignored}
                    />
                </ObservationField>
                <ObservationField
                    id={`${observationKey}-total`}
                    label="Total /10 feuilles"
                    className={concatClassName(styles[`${observationKey}-total`], 'col-4')}
                >
                    <Form.Number
                        value={total}
                        onChange={handleTotalChange}
                        state={validationErrors['total']?.type}
                        onFocus={() => handleFocus('total')}
                        required={!data.ignored}
                        readOnly={data.ignored}
                    />
                </ObservationField>
            </div>
            <ObservationErrors value={validationErrors} observationKey={observationKey} />
        </ObservationItem>
    );
};

Observation24.propTypes = {
    data: propTypes.any.isRequired,
    plot: plotDetailsPropTypes.isRequired,
    className: propTypes.string,
    onChange: propTypes.func
};

Observation24.defaultProps = {
    className: undefined,
    onChange: undefined
};

export default Observation24;
